import type { ProductType, ProductUnit, VatType } from '@/api/models';
import type {
    ProductBatchRequest,
    ProductCreateRequest,
    ProductVariantRequest,
} from '@/api/handlers/resources/products/requests';

/**
 * Types + pure helpers for the create-product form. All field state is held as
 * strings (raw input) and parsed into the typed `ProductCreateRequest` at
 * submit by `buildCreateRequest`. The matrix generator and request builder are
 * pure so they can be unit-tested directly. Cross-entity validation is the
 * server's job — `validateCreateForm` only does the light checks needed to
 * enable submit; the server's 400 `error` text is surfaced inline.
 */

export const TYPE_OPTIONS: ProductType[] = ['Simple', 'Variable'];

export const UNIT_OPTIONS: ProductUnit[] = [
    'Piece',
    'Kilogram',
    'Gram',
    'Liter',
    'Milliliter',
    'Meter',
    'Pack',
];

export const VAT_OPTIONS: VatType[] = ['Standard', 'Reduced', 'Exempt'];

/** Reuse the detail page's unit/VAT labels and the list's type labels. */
export const TYPE_LABEL_KEY: Record<ProductType, string> = {
    Simple: 'products.components.productTable.types.simple',
    Variable: 'products.components.productTable.types.variable',
};

export const UNIT_LABEL_KEY: Record<ProductUnit, string> = {
    Piece: 'products.detail.units.piece',
    Kilogram: 'products.detail.units.kilogram',
    Gram: 'products.detail.units.gram',
    Liter: 'products.detail.units.liter',
    Milliliter: 'products.detail.units.milliliter',
    Meter: 'products.detail.units.meter',
    Pack: 'products.detail.units.pack',
};

export const VAT_LABEL_KEY: Record<VatType, string> = {
    Standard: 'products.detail.vat.standard',
    Reduced: 'products.detail.vat.reduced',
    Exempt: 'products.detail.vat.exempt',
};

export const DEFAULT_UNIT: ProductUnit = 'Piece';
export const DEFAULT_VAT: VatType = 'Standard';

/** A FIFO stock layer being entered for a variant (raw string inputs). */
export type BatchDraft = {
    key: string;
    batchNumber: string;
    quantity: string;
    purchasePrice: string;
    supplierId: string | null;
    /**
     * Which branch received the goods. Required by the server (bos-api
     * BOS-126); seeded from the signed-in user's branch, or the main branch for
     * an org-wide admin, so the common case is one fewer decision.
     */
    branchId: string | null;
    receivedDate: string;
    expiryDate: string;
};

/** One variant being entered. `optionValues` maps axis id → chosen value id. */
export type VariantDraft = {
    key: string;
    optionValues: Record<string, string>;
    barcode: string;
    salePrice: string;
    wholesalePrice: string;
    minimumQuantity: string;
    weight: string;
    volume: string;
    saleEndDate: string;
    batches: BatchDraft[];
};

/** A selected variation axis and which of its values participate in the matrix. */
export type AxisDraft = {
    attributeTypeId: string;
    valueIds: string[];
};

/** Full form state held by the hook. */
export type CreateProductFormState = {
    type: ProductType;
    categoryId: string | null;
    name: string;
    sku: string;
    unit: ProductUnit;
    vatType: VatType;
    brandId: string | null;
    axes: AxisDraft[];
    variants: VariantDraft[];
};

export const makeBatchDraft = (
    key: string,
    branchId: string | null = null,
): BatchDraft => ({
    key,
    batchNumber: '',
    quantity: '',
    purchasePrice: '',
    supplierId: null,
    branchId,
    receivedDate: '',
    expiryDate: '',
});

export const makeVariantDraft = (
    key: string,
    optionValues: Record<string, string> = {},
): VariantDraft => ({
    key,
    optionValues,
    barcode: '',
    salePrice: '',
    wholesalePrice: '',
    minimumQuantity: '',
    weight: '',
    volume: '',
    saleEndDate: '',
    batches: [],
});

/** Cartesian product of N arrays. `[[a,b],[1,2]] → [[a,1],[a,2],[b,1],[b,2]]`. */
export const cartesianProduct = <T>(arrays: T[][]): T[][] =>
    arrays.reduce<T[][]>(
        (acc, current) =>
            acc.flatMap((combo) => current.map((value) => [...combo, value])),
        [[]],
    );

/**
 * Generate one variant draft per combination of the selected axis values
 * (cartesian across axes). Returns `[]` when there are no axes or any axis has
 * no selected values (nothing to combine). `makeKey` supplies unique React keys.
 */
export const generateVariantDrafts = (
    axes: AxisDraft[],
    makeKey: () => string,
): VariantDraft[] => {
    if (axes.length === 0 || axes.some((axis) => axis.valueIds.length === 0)) {
        return [];
    }
    const combos = cartesianProduct(axes.map((axis) => axis.valueIds));
    return combos.map((combo) => {
        const optionValues: Record<string, string> = {};
        axes.forEach((axis, index) => {
            optionValues[axis.attributeTypeId] = combo[index] as string;
        });
        return makeVariantDraft(makeKey(), optionValues);
    });
};

const parseOptionalNumber = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (trimmed === '') {
        return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const parseRequiredNumber = (value: string): number => {
    const parsed = Number(value.trim());
    return Number.isNaN(parsed) ? 0 : parsed;
};

const buildBatchRequest = (batch: BatchDraft): ProductBatchRequest => {
    const request: ProductBatchRequest = {
        quantity: parseRequiredNumber(batch.quantity),
        purchasePrice: parseRequiredNumber(batch.purchasePrice),
        // Never optional: `validateCreateForm` blocks submit until every batch
        // names one, so a blank here would be a form bug, not a legal request.
        branchId: batch.branchId ?? '',
    };
    if (batch.batchNumber.trim()) {
        request.batchNumber = batch.batchNumber.trim();
    }
    if (batch.supplierId) {
        request.supplierId = batch.supplierId;
    }
    if (batch.receivedDate) {
        request.receivedDate = batch.receivedDate;
    }
    if (batch.expiryDate) {
        request.expiryDate = batch.expiryDate;
    }
    return request;
};

const buildVariantRequest = (
    variant: VariantDraft,
    optionTypeIds: string[],
): ProductVariantRequest => {
    const request: ProductVariantRequest = {
        barcode: variant.barcode.trim(),
        optionValues: optionTypeIds.map((attributeTypeId) => ({
            attributeTypeId,
            attributeValueId: variant.optionValues[attributeTypeId] ?? '',
        })),
        salePrice: parseRequiredNumber(variant.salePrice),
        minimumQuantity: parseRequiredNumber(variant.minimumQuantity),
        batches: variant.batches.map(buildBatchRequest),
    };
    const wholesale = parseOptionalNumber(variant.wholesalePrice);
    if (wholesale !== undefined) {
        request.wholesalePrice = wholesale;
    }
    const weight = parseOptionalNumber(variant.weight);
    if (weight !== undefined) {
        request.weight = weight;
    }
    const volume = parseOptionalNumber(variant.volume);
    if (volume !== undefined) {
        request.volume = volume;
    }
    if (variant.saleEndDate) {
        request.saleEndDate = variant.saleEndDate;
    }
    return request;
};

/**
 * Assemble the typed `POST /products` body from the raw form state. Simple
 * products send no option axes and (by construction) one variant; Variable
 * products send the ordered axis ids and one value per axis per variant.
 */
export const buildCreateRequest = (
    state: CreateProductFormState,
): ProductCreateRequest => {
    const optionTypeIds =
        state.type === 'Variable'
            ? state.axes.map((axis) => axis.attributeTypeId)
            : [];

    const request: ProductCreateRequest = {
        name: state.name.trim(),
        type: state.type,
        unit: state.unit,
        vatType: state.vatType,
        categoryId: state.categoryId ?? '',
        optionTypeIds,
        variants: state.variants.map((variant) =>
            buildVariantRequest(variant, optionTypeIds),
        ),
    };
    if (state.sku.trim()) {
        request.sku = state.sku.trim();
    }
    if (state.brandId) {
        request.brandId = state.brandId;
    }
    return request;
};

/**
 * Which variant rows are wrong, and why, keyed by row `key`.
 *
 * <p>The form's only feedback used to be one line at the bottom of a page that
 * can hold a dozen variant cards, which left the reader to work out which card
 * it meant (BOS-140). These sit on the offending card.</p>
 *
 * <p>Duplicate option combinations only. Two variants of one product may share
 * a barcode — that is what the server's `BarcodeDiff` exists for — so the
 * barcode is not a uniqueness key here either (see bos-api BOS-131).</p>
 */
export const variantErrorKeys = (
    state: CreateProductFormState,
): Record<string, string> => {
    const errors: Record<string, string> = {};

    for (const variant of state.variants) {
        if (!variant.barcode.trim()) {
            errors[variant.key] = 'products.create.errors.barcodeRequired';
            continue;
        }
        if (parseOptionalNumber(variant.salePrice) === undefined) {
            errors[variant.key] = 'products.create.errors.salePriceRequired';
            continue;
        }
        if (state.type === 'Variable') {
            const missingValue = state.axes.some(
                (axis) => !variant.optionValues[axis.attributeTypeId],
            );
            if (missingValue) {
                errors[variant.key] =
                    'products.create.errors.optionValueRequired';
                continue;
            }
        }
        if (variant.batches.some((batch) => !batch.branchId)) {
            errors[variant.key] = 'products.create.errors.batchBranchRequired';
        }
    }

    // A combination is a set: listing size before colour describes the same
    // variant. Both rows are marked — neither is more wrong than the other.
    const seen = new Map<string, string[]>();
    for (const variant of state.variants) {
        const key = state.axes
            .map((axis) => variant.optionValues[axis.attributeTypeId] ?? '')
            .filter((value) => value.length > 0)
            .sort()
            .join('|');
        if (key.length === 0) {
            continue;
        }
        seen.set(key, [...(seen.get(key) ?? []), variant.key]);
    }
    for (const rowKeys of seen.values()) {
        if (rowKeys.length > 1) {
            for (const rowKey of rowKeys) {
                errors[rowKey] =
                    'products.create.errors.duplicateOptionCombination';
            }
        }
    }

    return errors;
};

/**
 * Light client-side validation — enough to enable submit and catch obvious
 * gaps. Returns the i18n key of the first problem, or `null` when the form is
 * submittable. The server still owns cross-entity validation.
 */
export const validateCreateForm = (
    state: CreateProductFormState,
): string | null => {
    if (!state.name.trim()) {
        return 'products.create.errors.nameRequired';
    }
    if (!state.categoryId) {
        return 'products.create.errors.categoryRequired';
    }
    if (state.type === 'Variable' && state.axes.length === 0) {
        return 'products.create.errors.axesRequired';
    }
    if (state.variants.length === 0) {
        return 'products.create.errors.variantsRequired';
    }
    // The server enforces `Simple ⇒ exactly one variant`; guard it here too so
    // the form can't submit a Simple product with extra rows the API rejects.
    if (state.type === 'Simple' && state.variants.length !== 1) {
        return 'products.create.errors.simpleSingleVariant';
    }
    for (const variant of state.variants) {
        if (!variant.barcode.trim()) {
            return 'products.create.errors.barcodeRequired';
        }
        if (parseOptionalNumber(variant.salePrice) === undefined) {
            return 'products.create.errors.salePriceRequired';
        }
        // Stock has to land somewhere. Without a branch the units are in the
        // ledger's inventory account and in no branch's stock view — the
        // discrepancy bos-api BOS-126 closed.
        if (variant.batches.some((batch) => !batch.branchId)) {
            return 'products.create.errors.batchBranchRequired';
        }
        if (state.type === 'Variable') {
            const hasAllValues = state.axes.every((axis) =>
                Boolean(variant.optionValues[axis.attributeTypeId]),
            );
            if (!hasAllValues) {
                return 'products.create.errors.optionValueRequired';
            }
        }
    }
    // Two rows describing the same variant. The server refuses it too
    // (bos-api BOS-131); catching it here is what lets the form point at the
    // rows rather than only refusing at the bottom of the page.
    const duplicate = Object.values(variantErrorKeys(state)).find(
        (key) => key === 'products.create.errors.duplicateOptionCombination',
    );
    return duplicate ?? null;
};
